import type { Row, SheetData } from 'write-excel-file/browser'

// ============================================================================
// Types
// ============================================================================

/** One column of a generated sheet. */
export interface ExcelColumn<TRow> {
  /** Header cell text. */
  header: string
  /**
   * Cell text for a data row.
   *
   * Returns a string on purpose. Every cell is written as text so values that merely look like
   * something else survive intact: a phone number keeps its leading `0` or `+`, and a name
   * starting with `=`, `+`, `-` or `@` cannot be read as a formula.
   */
  value: (row: TRow) => string | null | undefined
  /** Column width in characters. */
  width?: number
}

export interface ExcelExportOptions<TRow> {
  /** Download file name, including the .xlsx extension. */
  fileName: string
  /** Worksheet name; Excel rejects anything longer than 31 characters. */
  sheetName?: string
  columns: ExcelColumn<TRow>[]
  rows: TRow[]
}

// ============================================================================
// Constants
// ============================================================================

const MAX_SHEET_NAME_LENGTH = 31
const DEFAULT_COLUMN_WIDTH = 24

// ============================================================================
// Service
// ============================================================================

/**
 * Builds .xlsx files in the browser and hands them to the user.
 *
 * Generic on purpose: any admin list can pass its own columns, so a second export feature does
 * not need a second endpoint that returns a file. The backend stays a data source.
 */
export const excelExportService = {
  /** Builds the workbook and triggers the browser download. */
  async download<TRow>(options: ExcelExportOptions<TRow>): Promise<void> {
    // Named rather than `this.toBlob` so a destructured `const { download } = ...` still works.
    const blob = await excelExportService.toBlob(options)
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = options.fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },

  /** Builds the workbook and returns it, for callers that do not want a download. */
  async toBlob<TRow>({
    sheetName = 'Sheet1',
    columns,
    rows,
  }: ExcelExportOptions<TRow>): Promise<Blob> {
    // Loaded on demand: the export path is rare, and the library is large enough that bundling it
    // into the entry chunk would slow every page that never exports anything.
    const { default: writeXlsxFile } = await import('write-excel-file/browser')

    const headerRow: Row = columns.map((column) => ({
      value: column.header,
      type: String,
      fontWeight: 'bold' as const,
      backgroundColor: '#F1F5F9',
    }))

    const dataRows: Row[] = rows.map((row) =>
      columns.map((column) => ({
        value: column.value(row) ?? '',
        type: String,
      }))
    )

    const data: SheetData = [headerRow, ...dataRows]

    return writeXlsxFile(data, {
      sheet: sheetName.trim().slice(0, MAX_SHEET_NAME_LENGTH) || 'Sheet1',
      columns: columns.map((column) => ({ width: column.width ?? DEFAULT_COLUMN_WIDTH })),
      stickyRowsCount: 1,
    }).toBlob()
  },
}
